// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Plugins
import {
  DEVICE_COMMUNICATION_THRESHOLD,
  feederAutomationModeToTagType,
  feederAutomationModeToText,
  isEquipmentRunning,
  permissiveStatusToIndicatorStatus,
  runningStatusToIndicatorStatus,
} from '../../../../../../plugins/crusher';

// Types
import { CrusherRunningSStatus } from '../../../../../../types/crusher';
import { Theme } from '../../../../../../types/theme';

// Components
import { Col } from '../../../../../../layout/elements/Col';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import { Row } from '../../../../../../layout/elements/Row';
import {
  StatusIndicator,
  StatusIndicatorSize,
} from '../../../../../../layout/elements/StatusIndicator';
import { Tag } from '../../../../../../layout/elements/Tag';
import { Thermometer } from '../../../../../../layout/elements/Thermometer';

// View
import { FeederProps } from './Feeder.types';
import {
  BinFeederAutoModeContainer,
  BinFeederContainer,
  BinFeederTitleContainer,
  PermissiveItem,
  PermissiveListContainer,
  PermissivesContainer,
  PermissiveTitleContainer,
  RunningStatusContainer,
  RunningStatusList,
  ThermometerContainer,
} from './Feeder.style';

function Feeder({
  className,
  unitData,
  edgeStatus,
  testId,
}: FeederProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const { isXs } = useBreakpoint();

  // Theme
  const theme: Theme = useTheme();

  /**
   * Render a permissive item with the given name and value.
   * @param {string} permissiveName - The name of the permissive.
   * @param {boolean | null | undefined} permissive - The value of the permissive.
   * @returns {JSX.Element} A JSX element.
  */
  const renderPermissive = (
    permissiveName: string,
    permissive: boolean | null | undefined,
  ): JSX.Element => (
    <PermissiveItem
      data-testid="feeder-drawer-permissive-item"
      status={permissiveStatusToIndicatorStatus(
        permissive,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      )}
    >
      <StatusIndicator
        text={t(`view.crusher.vsi.drawers.feeder.${permissiveName}`)}
        size={isXs ? StatusIndicatorSize.sm : StatusIndicatorSize.lg}
        status={permissiveStatusToIndicatorStatus(
          permissive,
          edgeStatus ? edgeStatus.edgeStatus : null,
          unitData?.timestamp,
        )}
      />
    </PermissiveItem>
  );

  /**
   * Render a running status permissive item with the given name and value.
   * @param {string} runningStatusName - The name of the running status.
   * @param {CrusherRunningSStatus | null | undefined} runningStatus - The value of the running
   * status.
   * @returns {JSX.Element} A JSX element.
  */
  const renderRunningStatusPermissive = (
    runningStatusName: string,
    runningStatus: CrusherRunningSStatus | null | undefined,
  ): JSX.Element => (
    <PermissiveItem
      data-testid="feeder-drawer-permissive-item"
      status={runningStatusToIndicatorStatus(
        runningStatus,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      )}
    >
      <StatusIndicator
        text={t(`view.crusher.vsi.drawers.feeder.${runningStatusName}`)}
        size={isXs ? StatusIndicatorSize.sm : StatusIndicatorSize.lg}
        status={runningStatusToIndicatorStatus(
          runningStatus,
          edgeStatus ? edgeStatus.edgeStatus : null,
          unitData?.timestamp,
        )}
      />
    </PermissiveItem>
  );

  return (
    <BinFeederContainer
      data-testid={testId}
      className={className}
    >
      <Row>
        <Col>
          <RunningStatusContainer data-testid={`${testId}-running-status-container`}>
            <Paragraph
              size={isXs ? ParagraphSize.sm : ParagraphSize.md}
              weight={ParagraphWeight.bold}
            >
              {t('view.crusher.vsi.drawers.feeder.runningStatus')}
            </Paragraph>
            <RunningStatusList>
              <StatusIndicator
                testId="feeder-running-status-indicator"
                text={t('view.crusher.vsi.drawers.feeder.title')}
                status={
                  runningStatusToIndicatorStatus(
                    unitData?.feederRunningStatus,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )
                }
              />
              <StatusIndicator
                testId="bin-feeder-running-status-indicator"
                text={t('view.crusher.vsi.drawers.feeder.binFeeder')}
                status={
                  runningStatusToIndicatorStatus(
                    unitData?.binFeederRunningStatus,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )
                }
              />
            </RunningStatusList>
          </RunningStatusContainer>
        </Col>
        <Col>
          <BinFeederTitleContainer data-testid={`${testId}-bin-feeder-title-container`}>
            <Paragraph
              size={isXs ? ParagraphSize.sm : ParagraphSize.md}
              weight={ParagraphWeight.bold}
            >
              {t('view.crusher.vsi.drawers.feeder.binFeeder')}
            </Paragraph>
          </BinFeederTitleContainer>
        </Col>
        <Col>
          <BinFeederAutoModeContainer data-testid={`${testId}-auto-mode-container`}>
            <Paragraph
              size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
              weight={ParagraphWeight.medium}
              color={theme.color.crusherDashboard.feeder.textColor}
            >
              {t('view.crusher.vsi.drawers.feeder.binFeederAutoMode')}
            </Paragraph>
            <Tag
              testId="bin-feeder-auto-mode-tag"
              type={
                feederAutomationModeToTagType(
                  unitData?.binFeederAutoMode,
                  edgeStatus ? edgeStatus.edgeStatus : null,
                  unitData?.timestamp,
                )
              }
              text={feederAutomationModeToText(unitData?.binFeederAutoMode)}
            />
          </BinFeederAutoModeContainer>
        </Col>
        <Col>
          <ThermometerContainer data-testid={`${testId}-thermometer-container`}>
            <Thermometer
              testId="bin-feeder-speed-vfd-thermometer"
              unit="%"
              running={
                isEquipmentRunning(
                  edgeStatus,
                  unitData?.timestamp,
                  DEVICE_COMMUNICATION_THRESHOLD,
                )
              }
              title={`${t('view.crusher.vsi.drawers.feeder.binFeederSpeedVfd')}`}
              minValue={0}
              maxValue={100}
              value={unitData?.binFeederSpeedVFD}
            />
          </ThermometerContainer>
        </Col>
        <PermissivesContainer data-testid={`${testId}-permissives-container`}>
          <Row>
            <Col xs={24} xl={12}>
              <PermissiveListContainer>
                <PermissiveTitleContainer>
                  <StatusIndicator
                    testId="feeder-permissive-indicator"
                    text={t('view.crusher.vsi.drawers.feeder.feederPermissive')}
                    size={StatusIndicatorSize.lg}
                    status={
                      permissiveStatusToIndicatorStatus(
                        unitData?.feederPermissive,
                        edgeStatus ? edgeStatus.edgeStatus : null,
                        unitData?.timestamp,
                      )
                    }
                  />
                </PermissiveTitleContainer>
                {renderPermissive('feederStarterFault', unitData?.feederStarterFault)}
                {/* Crusher Motor Status */}
                {renderRunningStatusPermissive('crusherMotorRunning', unitData?.crusherMotorStatus)}
                {renderPermissive('crusherMotor1Fault', unitData?.crusherMotor1Fault)}
                {renderPermissive('crusherMotor2Fault', unitData?.crusherMotor2Fault)}
                {renderPermissive('eStop', unitData?.eStop)}
                {renderPermissive('highVibrationPermissive', unitData?.highVibrationPermissive)}
                {renderPermissive('vibrationTransducerFault', unitData?.vibrationTransducerFault)}
                {/* Discharge Running Status */}
                {renderRunningStatusPermissive(
                  'dischargeRunningStatus',
                  unitData?.dischargeRunningStatus,
                )}
                {renderPermissive('lidOpenStatus', unitData?.lidOpenStatus)}
                {renderPermissive('mandoorClosed', unitData?.mandoorClosed)}
                {renderPermissive('beltSlipPermissive', unitData?.beltSlipPermissive)}
              </PermissiveListContainer>
            </Col>

            <Col xs={24} xl={12}>
              <PermissiveListContainer borderLeft>
                {renderPermissive('lubeOilLevelLow', unitData?.lubeOilLevelLow)}
                {renderPermissive('lowLubeOilGPM1', unitData?.lowLubeOilGPM1)}
                {renderPermissive('lowLubeOilGPM2', unitData?.lowLubeOilGPM2)}
                {renderPermissive(
                  'lubeHighReturnTempPermissive',
                  unitData?.lubeHighReturnTempPermissive,
                )}
                {renderPermissive('binFeederPermissive', unitData?.binFeederPermissive)}
                {renderRunningStatusPermissive(
                  'vsiFeedBeltRunning',
                  unitData?.feederRunningStatus,
                )}
                {renderRunningStatusPermissive(
                  'binFeederRunningStatus',
                  unitData?.binFeederRunningStatus,
                )}
              </PermissiveListContainer>
            </Col>

          </Row>
        </PermissivesContainer>
      </Row>
    </BinFeederContainer>
  );
}

export { Feeder };
